import CommonChip from '@app/src/components/CommonChip'
import type { ScrapedAnswer } from '@app/src/types/resourceExplorer'
import { EvidenceType, SourceScope } from '@app/src/types/resourceExplorer'
import { insertIf } from '@app/src/utils/helpersTs'
import { Stack, Typography } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'

interface AnswerEvidenceDetailsProps {
  scrapedAnswer: ScrapedAnswer
}

const shownSourceScopes = [SourceScope.Entity, SourceScope.ControllingParent, SourceScope.Other]

export const hasEvidenceDetails = (scrapedAnswer: ScrapedAnswer): boolean =>
  Boolean(
    (scrapedAnswer.evidenceType && scrapedAnswer.evidenceType !== EvidenceType.None) ||
    (scrapedAnswer.sourceScope && shownSourceScopes.includes(scrapedAnswer.sourceScope)) ||
    scrapedAnswer.reportingYear ||
    scrapedAnswer.basis,
  )

/** The grading behind an AI answer, one labelled row per known field. */
export const AnswerEvidenceDetails: React.FC<AnswerEvidenceDetailsProps> = ({ scrapedAnswer }) => {
  const { formatMessage } = useIntl()
  const { evidenceType, sourceScope, reportingYear, basis } = scrapedAnswer

  const rows = [
    ...insertIf(Boolean(evidenceType && evidenceType !== EvidenceType.None), {
      key: 'evidence',
      label: formatMessage({ id: 'aiScreening.evidence.rows.evidence' }),
      value: formatMessage({ id: `aiScreening.evidence.type.${evidenceType}` }),
      tooltip: formatMessage({ id: `aiScreening.evidence.typeDescription.${evidenceType}` }),
    }),
    ...insertIf(Boolean(sourceScope && shownSourceScopes.includes(sourceScope)), {
      key: 'source',
      label: formatMessage({ id: 'aiScreening.evidence.rows.source' }),
      value: formatMessage({ id: `aiScreening.evidence.scope.${sourceScope}` }),
      tooltip: formatMessage({ id: `aiScreening.evidence.scopeDescription.${sourceScope}` }),
    }),
    ...insertIf(Boolean(reportingYear), {
      key: 'year',
      label: formatMessage({ id: 'aiScreening.evidence.rows.year' }),
      value: String(reportingYear),
      tooltip: undefined,
    }),
    ...insertIf(Boolean(basis), {
      key: 'basis',
      label: formatMessage({ id: 'aiScreening.evidence.rows.basis' }),
      value: basis ?? '',
      tooltip: undefined,
    }),
  ]

  return (
    <Stack spacing={2}>
      {rows.map(row => (
        <Stack key={row.key} direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="body1">{row.label}</Typography>
          <CommonChip variant="outlined" label={row.value} tooltip={row.tooltip} />
        </Stack>
      ))}
    </Stack>
  )
}
